import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Grid, CircularProgress, Button } from "@mui/material";
import DeceasedDetail from "./DeceasedDetail";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../../store/Index";
import apiService from "../../../../../service/Api";
import { IStreamDetails } from "../../../../../types/streams";
import { toast } from "react-toastify";

import PackageIcon from "../../../../../assets/icons/gift.svg";
import UserIcon from "../../../../../assets/icons/user-square.svg";
import Calendar from "../../../../../assets/icons/calendar-2.svg";
import DynamicThumbnailCard from "../../thumbnail/ThumbnailCard";

type Props = {
  streamId?: string;
};

const BookingAndDeceasedDetails: React.FC<Props> = ({ streamId }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [streamDetails, setStreamDetails] = useState<IStreamDetails | null>(
    null,
  );
  const [slug, setSlug] = useState<string | null>("");
  const [thumbnailRefreshTrigger] = useState(0);
  const params = useParams<{ id: string }>();

  const resolvedStreamId = useMemo(
    () => streamId ?? params.id ?? "",
    [streamId, params.id],
  );

  const fetchBookingDetails = useCallback(async () => {
    if (!resolvedStreamId) return;
    setError(false);
    setIsLoading(true);
    try {
      const response = await apiService.get<IStreamDetails>(
        `/api/streams/stream/${resolvedStreamId}/`,
      );

      setStreamDetails(response.data);
    } catch (error) {
      console.error("Failed to load stream:", error);
      toast.error("Failed to load stream. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [resolvedStreamId]);

  useEffect(() => {
    fetchBookingDetails();
  }, [fetchBookingDetails]);

  useEffect(() => {
    if (!streamDetails) return;

    const deceased = streamDetails.booking?.deceased?.[0];

    const nameSource = deceased
      ? `${deceased.first_name} ${deceased.surname}`
      : streamDetails.title;

    if (!nameSource) return;

    const slug = nameSource
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

    setSlug(slug);
  }, [streamDetails]);

  if (!resolvedStreamId) {
    return (
      <Box sx={{ p: 2, backgroundColor: reduxTheme.primary_colors[900] }}>
        <Typography>Stream ID missing.</Typography>
      </Box>
    );
  }

  return (
    <Box
      component={"section"}
      sx={{
        backgroundColor: reduxTheme.primary_colors[900],
        p: 1,
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          "& .MuiTypography-root": {
            fontSize: "16px",
            fontWeight: "600",
          },
        }}
      >
        {streamDetails && (
          <>
            <DeceasedDetail
              hasLink={false}
              detailHeader=""
              detailValue={
                streamDetails.booking?.deceased?.[0]
                  ? `${streamDetails.booking.deceased[0].first_name} ${streamDetails.booking.deceased[0].surname}`
                  : streamDetails.title || "N/A"
              }
              url=""
            />
            <Box
              sx={{
                "& .MuiTypography-root": {
                  fontSize: "14px",
                  fontWeight: "400",
                },
              }}
            >
              <DeceasedDetail
                hasLink={false}
                detailHeader=""
                detailValue={
                  streamDetails.booking.deceased.length === 0
                    ? "N/A"
                    : `${new Date(
                        streamDetails.booking.deceased[0].date_of_birth,
                      ).getFullYear()}-${new Date(
                        streamDetails.booking.deceased[0].date_of_death,
                      ).getFullYear()}`
                }
                url=""
              />
            </Box>
          </>
        )}
      </Box>
      {isLoading && (
        <Box display={"flex"} justifyContent={"center"} pt={2} pb={2}>
          <CircularProgress size={20} />
        </Box>
      )}

      {error && !isLoading && (
        <Box pt={1} pb={1}>
          <Typography variant="body1" align="center">
            Something went wrong in collecting details
          </Typography>
          <Box display={"flex"} justifyContent={"center"} mt={1}>
            <Button
              onClick={fetchBookingDetails}
              variant="contained"
              sx={{ ml: "auto", mr: "auto" }}
            >
              Retry
            </Button>
          </Box>
        </Box>
      )}

      {!error && !isLoading && streamDetails && (
        <Grid container gap={5} justifyContent="flex-start">
          <Grid container direction="column" gap={0.1}>
            {/* Each row: header + value */}
            <Box
              display="grid"
              gridTemplateColumns="150px 1fr"
              alignItems="center"
              gap={1}
            >
              <DeceasedDetail
                hasLink={false}
                detailHeader="Stream Date"
                headerIcon={
                  <img
                    src={Calendar}
                    alt="User Icon"
                    style={{ width: 18, height: 18 }}
                  />
                }
                detailValue=""
                url=""
              />
              <DeceasedDetail
                hasLink={false}
                detailHeader=""
                detailValue={new Date(streamDetails.date).toLocaleDateString(
                  "en-GB",
                  {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  },
                )}
                url=""
              />
            </Box>

            <Box
              display="grid"
              gridTemplateColumns="150px 1fr"
              alignItems="center"
              gap={1}
            >
              <DeceasedDetail
                hasLink={false}
                detailHeader="Package"
                headerIcon={
                  <img
                    src={PackageIcon}
                    alt="User Icon"
                    style={{ width: 18, height: 18 }}
                  />
                }
                detailValue=""
                url=""
              />
              <DeceasedDetail
                hasLink={false}
                detailHeader=""
                detailValue={
                  streamDetails.booking.package_item
                    ? streamDetails.booking.package_item.name
                    : "N/A"
                }
                url=""
              />
            </Box>

            <Box
              display="grid"
              gridTemplateColumns="150px 1fr"
              alignItems="center"
              gap={1}
            >
              <DeceasedDetail
                hasLink={false}
                detailHeader="Contact Person"
                headerIcon={
                  <img
                    src={UserIcon}
                    alt="User Icon"
                    style={{ width: 18, height: 18 }}
                  />
                }
                detailValue=""
                url=""
              />
              <DeceasedDetail
                hasLink={false}
                detailHeader=""
                detailValue={
                  streamDetails.booking.contact
                    ? `${streamDetails.booking.contact.first_name} ${streamDetails.booking.contact.last_name}`
                    : "N/A"
                }
                url=""
              />
            </Box>
          </Grid>

          <Grid container sx={{ ml: 5 }} direction="column" gap={1}>
            {slug && (
              <Button
                sx={{ backgroundColor: "info.main", borderRadius: "8px" }}
                variant="contained"
                href={`/memorial-page/${streamDetails.booking.id}/${slug}/`}
              >
                Memorial Page
              </Button>
            )}

            <Button
              sx={{ backgroundColor: "#AF52DE", borderRadius: "8px" }}
              variant="contained"
              href={`/bookings/${streamDetails.booking.id}`}
            >
              Booking Details
            </Button>

            <Button
              sx={{ borderRadius: "8px" }}
              variant="contained"
              href={`/stream-management/view/${streamDetails.id}`}
            >
              Stream Details
            </Button>
          </Grid>

          <Grid sx={{ mt: -8, ml: 5 }}>
            <DynamicThumbnailCard
              refreshTrigger={thumbnailRefreshTrigger}
              streamData={streamDetails}
              streamId={streamDetails.id}
              height={250}
              width={350}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default BookingAndDeceasedDetails;
